/**
 * Turning stored alerts into what the operator reads.
 *
 * Everything decided here is pure, because everything decided here is easy to get subtly and
 * dangerously wrong: which name to show, how many occurrences to admit to, and — above all — which
 * timestamps are real.
 */

import type { FieldDeviceKind, RobotSummary } from '../../../services/api/domains/robots.api'
import type { RobotAlertItem } from '../../../services/api/robot-alerts-store'

/** Roster rows indexed by robot id, so a label lookup is O(1) per render. */
export type RosterIndex = ReadonlyMap<string, RobotSummary>

export function indexRoster(robots: readonly RobotSummary[]): RosterIndex {
  return new Map(robots.map(r => [r.id, r]))
}

/**
 * The label for an alert, in preference order.
 *
 * `alert.name` first because it was captured AT THE INCIDENT: an alert is a record of something
 * that happened, and it must keep saying who it happened to after the device is renamed, reassigned
 * or deleted. Only when the alert carries none — every alert written before the `Alert` schema
 * declared the field — do we reach for the live roster, and there the WEARER outranks the device,
 * because an operator answering an alarm needs the person.
 *
 * Returns null rather than a robotId. A raw ObjectId is not a name; showing one is the defect this
 * function exists to end, and a caller that renders "Unknown device" is telling the truth while an
 * id pretends to be an answer.
 */
export function resolveAlertLabel(alert: RobotAlertItem, roster: RosterIndex): string | null {
  if (alert.name) return alert.name

  const device = roster.get(alert.robotId)

  return device?.wearerName ?? device?.name ?? null
}

/** Which device class raised this alert, for the row's badge. Unknown → null, never guessed. */
export function resolveDeviceKind(alert: RobotAlertItem, roster: RosterIndex): FieldDeviceKind | null {
  return alert.kind ?? roster.get(alert.robotId)?.kind ?? null
}

/**
 * The occurrence times for one incident, newest first.
 *
 * Legacy alerts — written before occurrences were stored — have only two real timestamps:
 * `recordedAt` (the first sighting) and `lastSeenAt` (the most recent). Those two are returned and
 * nothing else.
 *
 * **Never interpolate the gap.** A `6×` legacy alert must not become six evenly-spaced times: those
 * four middle values would be fabricated measurements, indistinguishable on screen from recorded
 * ones, in a list an operator uses to reconstruct an incident. The same rule the readings module
 * applies to an invented zero heart-rate applies here — an absent measurement is omitted, never
 * synthesised. The count is reported separately and honestly by {@link countUnlisted}.
 */
export function occurrenceTimes(alert: RobotAlertItem): string[] {
  // A live repeat lands as a newer `lastSeenAt` BEFORE the poll refreshes `sightings`, so the union
  // is what makes the new occurrence a row now rather than five seconds later. `recordedAt` is not
  // unioned when sightings exist: the server seeds the array with it on create, and once the array
  // is capped the first-ever sighting would be an orphan far outside the listed run.
  const raw =
    alert.sightings && alert.sightings.length > 0
      ? [...alert.sightings, alert.lastSeenAt]
      : [alert.lastSeenAt, alert.recordedAt]

  // Dedupe on the INSTANT, not the string: the poll writes `toISOString()`, the live frame whatever
  // the broker serialised — the same millisecond in two spellings must not become two rows.
  const byMs = new Map<number, string>()

  for (const t of raw) {
    if (!t) continue

    const ms = Date.parse(t)

    if (Number.isFinite(ms) && !byMs.has(ms)) byMs.set(ms, t)
  }

  return [...byMs.entries()].sort((a, b) => b[0] - a[0]).map(([, t]) => t)
}

/**
 * How many occurrences happened that this list does not show.
 *
 * `sightingCount` is the authority for "how many", never `sightings.length` — the stored array is
 * capped, so on a helmet left off for hours the count runs into the thousands while the array holds
 * the newest twenty. Reporting the array length would under-report a flood by three orders of
 * magnitude, which is precisely the situation an operator most needs stated plainly.
 */
export function countUnlisted(alert: RobotAlertItem, listed: number): number {
  return Math.max(0, (alert.sightingCount ?? 1) - listed)
}

/**
 * A wall-clock time for one occurrence.
 *
 * Deliberately absolute, where the block header stays relative. Six repetitions of "5 min ago" is
 * unreadable and answers nothing; six clock times is the list that was actually asked for.
 */
export function formatClockTime(iso: string, locale?: string): string {
  const ms = Date.parse(iso)

  if (!Number.isFinite(ms)) return ''

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ms))
}
